import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, MousePointerClick, ExternalLink, TrendingUp } from "lucide-react";
import api from "@/lib/api";

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-brand font-mono-label">loading analytics...</div>;

  const maxCat = d.views_by_category[0]?.views || 1;

  return (
    <div>
      <div className="mb-8"><div className="label-mono mb-1">Insights</div><h1 className="font-display font-extrabold text-white text-3xl">Analytics</h1><p className="text-slate-400 text-sm mt-1">Actionable content performance, not vanity metrics.</p></div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="rounded-xl border border-ink-600 bg-ink-900 p-5"><div className="flex items-center gap-2 text-slate-400 text-xs font-mono-label uppercase mb-2"><Eye size={14} /> Total Views</div><div className="font-display font-extrabold text-white text-3xl">{d.total_views.toLocaleString()}</div></div>
        <div className="rounded-xl border border-ink-600 bg-ink-900 p-5"><div className="flex items-center gap-2 text-slate-400 text-xs font-mono-label uppercase mb-2"><MousePointerClick size={14} /> CTA Clicks</div><div className="font-display font-extrabold text-white text-3xl">{d.cta_clicks.toLocaleString()}</div></div>
        <div className="rounded-xl border border-ink-600 bg-ink-900 p-5"><div className="flex items-center gap-2 text-slate-400 text-xs font-mono-label uppercase mb-2"><ExternalLink size={14} /> Outbound Clicks</div><div className="font-display font-extrabold text-white text-3xl">{d.outbound_clicks.toLocaleString()}</div></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-ink-600 bg-ink-900 p-6">
          <h2 className="font-display font-bold text-white text-lg mb-5 flex items-center gap-2"><TrendingUp size={18} className="text-brand" /> Top Articles by Views</h2>
          <div className="space-y-1">
            {d.top_articles.map((a, i) => {
              const rate = a.views ? ((0 / a.views) * 100) : 0;
              return (
                <Link key={a.slug} to={`/article/${a.slug}`} target="_blank" className="flex items-center gap-4 py-3 border-b border-ink-700 last:border-0 hover:bg-ink-800/40 rounded px-2 transition-colors">
                  <span className="font-display font-extrabold text-lg text-ink-600 w-6">{i + 1}</span>
                  <div className="flex-1 min-w-0"><div className="text-sm text-white truncate">{a.title}</div></div>
                  <span className="text-sm font-bold text-brand shrink-0">{a.views.toLocaleString()}</span>
                </Link>
              );
            })}
            {d.top_articles.length === 0 && <p className="text-slate-500 text-sm">No data yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-ink-600 bg-ink-900 p-6">
          <h2 className="font-display font-bold text-white text-lg mb-5">Views by Category</h2>
          <div className="space-y-4">
            {d.views_by_category.map((c) => (
              <div key={c._id}>
                <div className="flex justify-between text-xs mb-1.5"><span className="text-slate-300 capitalize">{(c._id || "uncategorized").replace(/-/g, " ")}</span><span className="text-slate-500">{c.views.toLocaleString()}</span></div>
                <div className="h-2 bg-ink-700 rounded-full overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${(c.views / maxCat) * 100}%` }} /></div>
              </div>
            ))}
            {d.views_by_category.length === 0 && <p className="text-slate-500 text-sm">No data yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-ink-600 bg-ink-900 p-6">
          <h2 className="font-display font-bold text-white text-lg mb-5">Traffic Sources</h2>
          <div className="space-y-3">
            {d.traffic_sources.map((s) => {
              const max = d.traffic_sources[0]?.c || 1;
              return (<div key={s._id}><div className="flex justify-between text-xs mb-1"><span className="text-slate-300 capitalize">{s._id}</span><span className="text-slate-500">{s.c}</span></div><div className="h-2 bg-ink-700 rounded-full overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${(s.c / max) * 100}%` }} /></div></div>);
            })}
            {d.traffic_sources.length === 0 && <p className="text-slate-500 text-sm">No traffic data yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-ink-600 bg-ink-900 p-6">
          <h2 className="font-display font-bold text-white text-lg mb-5">CTA Performance</h2>
          <div className="space-y-3">
            {d.cta_performance.map((c) => (
              <div key={c._id} className="flex items-center justify-between py-2 border-b border-ink-700 last:border-0"><span className="text-sm text-slate-300">{c._id}</span><span className="text-sm font-bold text-brand">{c.c} clicks</span></div>
            ))}
            {d.cta_performance.length === 0 && <p className="text-slate-500 text-sm">No CTA clicks tracked yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
